import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext.jsx";
import { US_STATES } from "../../../utils/usStates";
import { SPORTS } from "../../../utils/sports";
import { PROGRAM_TYPES } from "../../../utils/programTypes";
import { AUDIENCES } from "../../../utils/audiences";
import { COMPETITION_LEVELS } from "../../../utils/competitionLevels";
import api from "@api";
import "./ManageListing.css";

function ManageListing() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();

  const isFreePlan = user?.plan === "free";
  const isTrialPlan = user?.plan === "trial";
  const canShowLeagueTrial = isFreePlan || isTrialPlan;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const [activeProgramIndex, setActiveProgramIndex] = useState(0);
  const [activeProgramTab, setActiveProgramTab] = useState("basic");
  const [openProgramPanel, setOpenProgramPanel] = useState(null);
  const [editingScheduleIndex, setEditingScheduleIndex] = useState(null);
  const [editingVenueIndex, setEditingVenueIndex] = useState(null);
  const [draggedProgramIndex, setDraggedProgramIndex] = useState(null);
  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const [locationsInput, setLocationsInput] = useState("");

  const [form, setForm] = useState({
    name: "",
    contact_name: "",
    country: "US",
    city: "",
    county: "",
    state: "",
    locations: [],
    venues: [],
    phone_number: "",
    email: "",
    website_url: "",
    register_url: "",
    register_label: "",
    instagram_url: "",
    facebook_url: "",
    youtube_url: "",
    twitter_url: "",
    description: "",
    directory_logo: null,
    existing_directory_logo: "",
    gallery_images: [],
    existing_gallery_images: [],
    programs: [],
  });

  useEffect(() => {
    const fetchLeague = async () => {
      const res = await api.get(`/api/directory/leagues/${id}`);
      const league = res.data;

      setForm({
        name: league.name || "",
        contact_name: league.contact_name || "",
        country: league.country || "US",
        city: league.city || "",
        county: league.county || "",
        state: league.state || "",
        locations: league.locations || [],
        venues: Array.isArray(league.venues) ? league.venues : [],
        phone_number: league.phone_number || "",
        email: league.email || "",
        website_url: league.website_url || "",
        register_url: league.register_url || "",
        register_label: league.register_label || "",
        instagram_url: league.instagram_url || "",
        facebook_url: league.facebook_url || "",
        youtube_url: league.youtube_url || "",
        twitter_url: league.twitter_url || "",
        description: league.description || "",
        directory_logo: null,
        existing_directory_logo: league.directory_logo || "",
        gallery_images: [],
        existing_gallery_images: league.gallery_images || [],
        programs: (league.programs || []).map((program) => {
          let normalizedGender = [];

          if (Array.isArray(program.gender)) {
            normalizedGender = program.gender;
          } else if (typeof program.gender === "string") {
            const trimmed = program.gender.trim();

            if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
              normalizedGender = trimmed
                .slice(1, -1)
                .split(",")
                .map((g) => g.trim().replace(/^"|"$/g, ""))
                .filter(Boolean);
            } else if (trimmed) {
              normalizedGender = [trimmed];
            }
          }

          return {
            ...program,
            gender: normalizedGender,
            contact_note: program.contact_note || "",
            schedule_details: Array.isArray(program.schedule_details)
              ? program.schedule_details.map((schedule) => ({
                ...schedule,
                days: Array.isArray(schedule.days)
                  ? schedule.days
                  : schedule.day
                    ? [schedule.day]
                    : [],
              }))
              : [],
            price: program.price || "",
            registration_url: program.registration_url || "",
            registration_status: program.registration_status || "",
            registration_deadline: program.registration_deadline || "",
            start_date: program.start_date || "",
            end_date: program.end_date || "",
            notes: program.notes || "",
            venues: Array.isArray(program.venues) ? program.venues : [],
          };
        }),
      });

      setLocationsInput((league.locations || []).join(", "));
      setLoading(false);
    };

    fetchLeague();
  }, [id]);

  if (loading) return <div className="ml-loading">Loading...</div>;

  const removeExistingGalleryImage = (indexToRemove) => {
    setForm((prev) => ({
      ...prev,
      existing_gallery_images: prev.existing_gallery_images.filter(
        (_, index) => index !== indexToRemove
      ),
    }));
    setIsDirty(true);
  };

  const removeNewGalleryImage = (indexToRemove) => {
    setForm((prev) => ({
      ...prev,
      gallery_images: prev.gallery_images.filter(
        (_, index) => index !== indexToRemove
      ),
    }));
    setIsDirty(true);
  };

  const removeExistingLogo = () => {
    setForm((prev) => ({
      ...prev,
      existing_directory_logo: "",
      directory_logo: null,
    }));
    setIsDirty(true);
  };

  const removeNewLogo = () => {
    setForm((prev) => ({
      ...prev,
      directory_logo: null,
    }));
    setIsDirty(true);
  };




  const normalizeCounty = (value = "") => {
    let county = value.trim();

    if (!county) return "";

    county = county
      .replace(/\s+/g, " ")
      .replace(/\bcounty\b\.?/gi, "")
      .replace(/\bco\.?\b/gi, "")
      .trim();

    if (!county) return "";

    county = county
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");

    return `${county} County`;
  };

  const addListingVenue = () => {
    setForm((prev) => ({
      ...prev,
      venues: [...(prev.venues || []), { name: "", map_url: "" }],
    }));
    setIsDirty(true);
  };

  const updateListingVenue = (index, field, value) => {
    setForm((prev) => {
      const nextVenues = [...(prev.venues || [])];

      nextVenues[index] = {
        ...nextVenues[index],
        [field]: value,
      };

      return {
        ...prev,
        venues: nextVenues,
      };
    });

    setIsDirty(true);
  };

  const removeListingVenue = (indexToRemove) => {
    setForm((prev) => ({
      ...prev,
      venues: (prev.venues || []).filter((_, index) => index !== indexToRemove),
    }));

    setIsDirty(true);
  };

  const handleSave = async () => {
    try {
      const county = normalizeCounty(form.county);

      const formData = new FormData();

      formData.append("name", form.name || "");
      formData.append("contact_name", form.contact_name || "");
      formData.append("country", form.country || "US");
      formData.append("city", form.city || "");
      formData.append("county", county || "");
      formData.append("state", form.state || "");
      formData.append("phone_number", form.phone_number || "");
      formData.append("email", form.email || "");
      formData.append("website_url", form.website_url || "");
      formData.append("register_url", form.register_url || "");
      formData.append("register_label", form.register_label || "");
      formData.append("instagram_url", form.instagram_url || "");
      formData.append("facebook_url", form.facebook_url || "");
      formData.append("youtube_url", form.youtube_url || "");
      formData.append("twitter_url", form.twitter_url || "");
      formData.append("description", form.description || "");

      formData.append(
        "locations",
        JSON.stringify(
          locationsInput
            .split(",")
            .map((c) => c.trim())
            .filter(Boolean)
        )
      );
      formData.append(
        "venues",
        JSON.stringify(
          (form.venues || []).filter((venue) => venue.name?.trim())
        )
      );
      formData.append("programs", JSON.stringify(form.programs || []));
      formData.append(
        "existing_gallery_images",
        JSON.stringify(form.existing_gallery_images || [])
      );
      formData.append(
        "existing_directory_logo",
        form.existing_directory_logo || ""
      );

      if (form.directory_logo) {
        formData.append("directory_logo", form.directory_logo);
      }

      if (form.gallery_images?.length) {
        form.gallery_images.forEach((file) => {
          formData.append("gallery_images", file);
        });
      }

      await api.put(`/api/directory/leagues/${id}`, formData);
      setIsDirty(false);
      alert("Listing updated successfully");
      navigate(`/leagues/${id}`);
    } catch (err) {
      alert(
        err.response?.data?.message ||
        "Something went wrong updating this listing."
      );
    }
  };

  const updateProgram = (index, key, value) => {
    const next = [...form.programs];
    next[index] = { ...next[index], [key]: value };
    setForm({ ...form, programs: next });
    setIsDirty(true);
  };

  const addProgram = () => {
    const newProgram = {
      sport: "",
      title: "",
      program_type: "",
      gender: [],
      competition_level: "",
      min_age: "",
      max_age: "",
      description: "",
      contact_name: "",
      email: "",
      phone_number: "",
      contact_note: "",
      schedule_details: [],
      season: "",
      price: "",
      registration_url: "",
      registration_status: "",
      registration_deadline: "",
      start_date: "",
      end_date: "",
      notes: "",
      venues: [{ name: "", map_url: "" }],
    };

    setForm((prev) => {
      const nextPrograms = [...prev.programs, newProgram];

      setActiveProgramIndex(nextPrograms.length - 1);
      setActiveProgramTab("basic");
      setEditingScheduleIndex(null);
      setEditingVenueIndex(null);

      return {
        ...prev,
        programs: nextPrograms,
      };
    });

    setIsDirty(true);
  };

  const removeProgram = (index) => {
    setForm((prev) => {
      const nextPrograms = prev.programs.filter((_, i) => i !== index);

      setActiveProgramIndex((currentIndex) => {
        if (nextPrograms.length === 0) return 0;
        if (index === currentIndex) return Math.max(0, currentIndex - 1);
        if (index < currentIndex) return currentIndex - 1;
        return currentIndex;
      });

      return {
        ...prev,
        programs: nextPrograms,
      };
    });

    setIsDirty(true);
  };


  const reorderPrograms = (fromIndex, toIndex) => {
    if (fromIndex === null || fromIndex === toIndex) return;

    setForm((prev) => {
      const nextPrograms = [...prev.programs];
      const [movedProgram] = nextPrograms.splice(fromIndex, 1);

      nextPrograms.splice(toIndex, 0, movedProgram);

      return {
        ...prev,
        programs: nextPrograms.map((program, index) => ({
          ...program,
          display_order: index,
        })),
      };
    });

    setActiveProgramIndex(toIndex);
    setDraggedProgramIndex(null);
    setIsDirty(true);
  };


  const activeProgram = form.programs[activeProgramIndex] || null;

  const toggleProgramPanel = (panel) => {
    setOpenProgramPanel((current) => (current === panel ? null : panel));
    setEditingScheduleIndex(null);
    setEditingVenueIndex(null);
  };




  const getProgramSummary = (program) => {
    const sportLabel =
      SPORTS.find((s) => s.key === program.sport)?.label || "Sport";

    const typeLabel =
      PROGRAM_TYPES.find((p) => p.key === program.program_type)?.label ||
      "Program";

    const competitionLabel = COMPETITION_LEVELS.find(
      (c) => c.key === program.competition_level
    )?.label;

    const audienceLabel =
      (program.gender || [])
        .map((g) => AUDIENCES.find((a) => a.key === g)?.label)
        .filter(Boolean)
        .join(", ") || "Audience";

    const ageLabel = program.min_age
      ? program.max_age
        ? `${program.min_age}-${program.max_age}`
        : `${program.min_age}+`
      : "Age";

    return [
      sportLabel,
      typeLabel,
      competitionLabel,
      audienceLabel,
      ageLabel,
    ]
      .filter(Boolean)
      .join(" · ");
  };

  const formatScheduleSummary = (schedule) => {
    const days = Array.isArray(schedule.days)
      ? schedule.days.map((day) => day.slice(0, 3)).join(", ")
      : schedule.day
        ? schedule.day.slice(0, 3)
        : "Days";

    const time =
      schedule.start_time || schedule.end_time
        ? `${schedule.start_time || "Start"} - ${schedule.end_time || "End"}`
        : "Time";

    return `${days} · ${time}`;
  };

  const formatVenueSummary = (venue) => {
    if (!venue?.name) return "Untitled venue";

    return venue.map_url ? `${venue.name} · Map added` : venue.name;
  };

  const handleStartTrial = async () => {
    try {
      if (!user?.organization_id) {
        alert("No organization found for this account.");
        return;
      }

      const res = await api.post(
        `/api/organizations/${user.organization_id}/start-trial`,
        {},
        { withCredentials: true }
      );

      await checkAuth();

      alert("Free trial started successfully.");
      navigate("/dashboard/seasons");
    } catch (err) {
      console.error("Start trial failed", err);
      alert(err.response?.data?.message || "Could not start free trial.");
    }
  };




  const handleViewListing = () => {
    if (isDirty) {
      const confirmed = window.confirm(
        "You have unsaved changes. If you leave, they will be lost."
      );

      if (!confirmed) return;
    }

    navigate(`/leagues/${id}`);
  };

  return (
    <div className="ml-page">
      <div className="ml-header">
        <h1>Manage Listing</h1>

        <div className="ml-header-actions">
          <button className="ml-btn-secondary" onClick={handleViewListing}>
            View Listing
          </button>
          <button className="ml-btn-primary" onClick={handleSave}>
            Save Changes
          </button>
        </div>
      </div>




      <div className="ml-card">
        <div
          className="ml-card-header"
          onClick={() => toggleSection("basic")}
        >
          <div>
            <h3>Basic Information</h3>
            <p>League name, location and description</p>
          </div>

          <span>{openSection === "basic" ? "▲" : "▼"}</span>
        </div>

        {openSection === "basic" && (
          <div className="ml-card-body">
            <input
              className="ml-input"
              placeholder="League Name"
              value={form.name}
              onChange={(e) => {
                setForm({ ...form, name: e.target.value });
                setIsDirty(true);
              }}
            />

            <div className="ml-row">
              <input
                className="ml-input"
                type="text"
                placeholder="County / Metro"
                value={form.county}
                onChange={(e) => {
                  setForm({ ...form, county: e.target.value });
                  setIsDirty(true);
                }}
                onBlur={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    county: normalizeCounty(e.target.value),
                  }));
                  setIsDirty(true);
                }}
              />

              <input
                className="ml-input"
                placeholder="City"
                value={form.city}
                onChange={(e) => {
                  setForm({ ...form, city: e.target.value });
                  setIsDirty(true);
                }}
              />
            </div>

            <input
              className="ml-input"
              placeholder="Other cities (e.g. Los Angeles, San Diego, Oakland)"
              value={locationsInput}
              onChange={(e) => {
                setLocationsInput(e.target.value);
                setIsDirty(true);
              }}
            />

            <select
              className="ml-input"
              value={form.state}
              onChange={(e) => {
                setForm({ ...form, state: e.target.value });
                setIsDirty(true);
              }}
            >
              <option value="">Select State</option>
              {US_STATES.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.name}
                </option>
              ))}
            </select>
            <div className="ml-field-group ml-full-width">
              <label className="ml-field-label">Main Venues / Locations</label>
              <p className="ml-help-text">
                Add the main facility or map locations shown on your public listing.
              </p>

              {(form.venues || []).map((venue, index) => (
                <div key={index} className="ml-mini-card">
                  <div className="ml-field-group">
                    <label className="ml-field-label">Venue Name</label>
                    <input
                      className="ml-input"
                      placeholder="Ex: Agape Tennis Academy"
                      value={venue.name || ""}
                      onChange={(e) =>
                        updateListingVenue(index, "name", e.target.value)
                      }
                    />
                  </div>

                  <div className="ml-field-group">
                    <label className="ml-field-label">Google Map URL</label>
                    <input
                      className="ml-input"
                      placeholder="https://maps.google.com/..."
                      value={venue.map_url || ""}
                      onChange={(e) =>
                        updateListingVenue(index, "map_url", e.target.value)
                      }
                    />
                  </div>

                  <button
                    type="button"
                    className="ml-small-danger"
                    onClick={() => removeListingVenue(index)}
                  >
                    Remove
                  </button>
                </div>
              ))}

              <button
                type="button"
                className="ml-btn-secondary"
                onClick={addListingVenue}
              >
                + Add Listing Venue
              </button>
            </div>
            <textarea
              className="ml-textarea"
              placeholder="Describe your league"
              value={form.description}
              onChange={(e) => {
                setForm({ ...form, description: e.target.value });
                setIsDirty(true);
              }}
            />
          </div>
        )}
      </div>

      <div className="ml-card">
        <div
          className="ml-card-header"
          onClick={() => toggleSection("contact")}
        >
          <div>
            <h3>Contact</h3>
            <p>Phone, email and communication details</p>
          </div>

          <span>{openSection === "contact" ? "▲" : "▼"}</span>
        </div>

        {openSection === "contact" && (
          <div className="ml-card-body">
            <div className="ml-field-group">
              <label className="ml-field-label">Contact Name</label>
              <input
                className="ml-input"
                placeholder="Ex: Coach Mike, League Director"
                value={form.contact_name}
                onChange={(e) => {
                  setForm({ ...form, contact_name: e.target.value });
                  setIsDirty(true);
                }}
              />
            </div>

            <div className="ml-field-group">
              <label className="ml-field-label">Phone Number</label>
              <input
                className="ml-input"
                placeholder="(555) 555-5555"
                value={form.phone_number}
                onChange={(e) => {
                  setForm({ ...form, phone_number: e.target.value });
                  setIsDirty(true);
                }}
              />
            </div>

            <div className="ml-field-group">
              <label className="ml-field-label">Email</label>
              <input
                className="ml-input"
                placeholder="contact@example.com"
                value={form.email}
                onChange={(e) => {
                  setForm({ ...form, email: e.target.value });
                  setIsDirty(true);
                }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="ml-card">
        <div
          className="ml-card-header"
          onClick={() => toggleSection("links")}
        >
          <div>
            <h3>Website & Social</h3>
            <p>Help players find you online</p>
          </div>

          <span>{openSection === "links" ? "▲" : "▼"}</span>
        </div>

        {openSection === "links" && (
          <div className="ml-card-body">
            <div className="ml-field-group">
              <label className="ml-field-label">Website</label>
              <input
                className="ml-input"
                placeholder="https://..."
                value={form.website_url}
                onChange={(e) => {
                  setForm({ ...form, website_url: e.target.value });
                  setIsDirty(true);
                }}
              />
            </div>

            <div className="ml-field-group">
              <label className="ml-field-label">Registration URL</label>
              <input
                className="ml-input"
                placeholder="https://..."
                value={form.register_url}
                onChange={(e) => {
                  setForm({ ...form, register_url: e.target.value });
                  setIsDirty(true);
                }}
              />
            </div>

            <div className="ml-field-group">
              <label className="ml-field-label">Registration Button Label</label>
              <input
                className="ml-input"
                placeholder="Ex: Register, Sign Up, Join League"
                value={form.register_label}
                onChange={(e) => {
                  setForm({ ...form, register_label: e.target.value });
                  setIsDirty(true);
                }}
              />
            </div>

            <div className="ml-field-group">
              <label className="ml-field-label">Instagram</label>
              <input
                className="ml-input"
                placeholder="Instagram URL"
                value={form.instagram_url}
                onChange={(e) => {
                  setForm({ ...form, instagram_url: e.target.value });
                  setIsDirty(true);
                }}
              />
            </div>

            <div className="ml-field-group">
              <label className="ml-field-label">Facebook</label>
              <input
                className="ml-input"
                placeholder="Facebook URL"
                value={form.facebook_url}
                onChange={(e) => {
                  setForm({ ...form, facebook_url: e.target.value });
                  setIsDirty(true);
                }}
              />
            </div>

            <div className="ml-field-group">
              <label className="ml-field-label">YouTube</label>
              <input
                className="ml-input"
                placeholder="YouTube URL"
                value={form.youtube_url}
                onChange={(e) => {
                  setForm({ ...form, youtube_url: e.target.value });
                  setIsDirty(true);
                }}
              />
            </div>

            <div className="ml-field-group">
              <label className="ml-field-label">Twitter / X</label>
              <input
                className="ml-input"
                placeholder="Twitter / X URL"
                value={form.twitter_url}
                onChange={(e) => {
                  setForm({ ...form, twitter_url: e.target.value });
                  setIsDirty(true);
                }}
              />
            </div>
          </div>
        )}
      </div>


      <div className="ml-card">
        <div
          className="ml-card-header"
          onClick={() => toggleSection("media")}
        >
          <div>
            <h3>Media</h3>
            <p>Directory logo and gallery images</p>
          </div>

          <span>{openSection === "media" ? "▲" : "▼"}</span>
        </div>

        {openSection === "media" && (
          <div className="ml-card-body">
            <div className="directory-stack">
              <label>Directory Logo</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  setForm({
                    ...form,
                    directory_logo: e.target.files?.[0] || null,
                  });
                  setIsDirty(true);
                }}
              />

              {form.existing_directory_logo && !form.directory_logo && (
                <div
                  className="directory-image-preview"
                  style={{ position: "relative", width: "80px", marginTop: "8px" }}
                >
                  <img
                    src={form.existing_directory_logo}
                    alt="Directory logo"
                    style={{
                      width: "80px",
                      height: "80px",
                      objectFit: "cover",
                      borderRadius: "8px",
                    }}
                  />
                  <button
                    type="button"
                    onClick={removeExistingLogo}
                    style={{
                      position: "absolute",
                      top: "-6px",
                      right: "-6px",
                      width: "22px",
                      height: "22px",
                      borderRadius: "50%",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    ×
                  </button>
                </div>
              )}

              {form.directory_logo && (
                <div
                  className="directory-image-preview"
                  style={{ position: "relative", width: "80px", marginTop: "8px" }}
                >
                  <img
                    src={URL.createObjectURL(form.directory_logo)}
                    alt="New directory logo"
                    style={{
                      width: "80px",
                      height: "80px",
                      objectFit: "cover",
                      borderRadius: "8px",
                    }}
                  />
                  <button
                    type="button"
                    onClick={removeNewLogo}
                    style={{
                      position: "absolute",
                      top: "-6px",
                      right: "-6px",
                      width: "22px",
                      height: "22px",
                      borderRadius: "50%",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    ×
                  </button>
                </div>
              )}
            </div>

            <div className="directory-stack">
              <label>Gallery Images (max 5)</label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  const newFiles = Array.from(e.target.files || []);

                  setForm((prev) => ({
                    ...prev,
                    gallery_images: [...prev.gallery_images, ...newFiles].slice(0, 5),
                  }));

                  setIsDirty(true);
                  e.target.value = "";
                }}
              />

              {form.existing_gallery_images?.length > 0 && (
                <div
                  className="directory-gallery-preview"
                  style={{
                    display: "flex",
                    gap: "8px",
                    flexWrap: "wrap",
                    marginTop: "8px",
                  }}
                >
                  {form.existing_gallery_images.map((img, index) => (
                    <div
                      key={`existing-${index}`}
                      style={{ position: "relative" }}
                    >
                      <img
                        src={img}
                        alt={`Gallery ${index + 1}`}
                        style={{
                          width: "80px",
                          height: "80px",
                          objectFit: "cover",
                          borderRadius: "8px",
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => removeExistingGalleryImage(index)}
                        style={{
                          position: "absolute",
                          top: "-6px",
                          right: "-6px",
                          width: "22px",
                          height: "22px",
                          borderRadius: "50%",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {form.gallery_images?.length > 0 && (
                <div
                  className="directory-gallery-preview"
                  style={{
                    display: "flex",
                    gap: "8px",
                    flexWrap: "wrap",
                    marginTop: "8px",
                  }}
                >
                  {form.gallery_images.map((file, index) => (
                    <div
                      key={`new-${index}`}
                      style={{ position: "relative" }}
                    >
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`New gallery ${index + 1}`}
                        style={{
                          width: "80px",
                          height: "80px",
                          objectFit: "cover",
                          borderRadius: "8px",
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => removeNewGalleryImage(index)}
                        style={{
                          position: "absolute",
                          top: "-6px",
                          right: "-6px",
                          width: "22px",
                          height: "22px",
                          borderRadius: "50%",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>


          </div>
        )}
      </div>







      <div className="ml-card">

        <div
          className="ml-card-header"
          onClick={() => toggleSection("programs")}
        >
          <div>
            <h3>Programs</h3>
            <p>Teams, divisions and competition levels</p>
          </div>

          <span>{openSection === "programs" ? "▲" : "▼"}</span>
        </div>

        {openSection === "programs" && (
          <div className="ml-card-body">
            <div className="ml-program-layout">
              <div className="ml-program-list">
                <button className="ml-btn-secondary" onClick={addProgram}>
                  + Add Program
                </button>
                {form.programs.map((program, index) => (
                  <div
                    key={program.id || index}
                    draggable
                    className={`ml-program-summary-card ${activeProgramIndex === index ? "active" : ""
                      } ${draggedProgramIndex === index ? "is-dragging" : ""}`}
                    onDragStart={() => setDraggedProgramIndex(index)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => reorderPrograms(draggedProgramIndex, index)}
                    onDragEnd={() => setDraggedProgramIndex(null)}
                    onClick={() => {
                      setActiveProgramIndex(index);
                      setActiveProgramTab("basic");
                      setEditingScheduleIndex(null);
                      setEditingVenueIndex(null);
                    }}
                  >



                    {/* Start of the the left program card */}
                    <div>
                      <strong>{program.title || "Untitled Program"}</strong>
                      <p>{getProgramSummary(program)}</p>

                      <button
                        type="button"
                        className="ml-program-remove"
                        onClick={(e) => {
                          e.stopPropagation();

                          const confirmed = window.confirm(
                            "Remove this program? This change will not be saved until you click Save Changes."
                          );

                          if (!confirmed) return;

                          removeProgram(index);
                        }}
                      >
                        Remove
                      </button>
                    </div>

                    <span>›</span>
                  </div>
                ))}
                {form.programs.length > 1 && (
                  <p className="ml-program-reorder-note">
                    Drag programs to reorder how they appear on your listing.
                  </p>
                )}
              </div>
              {/* End of the program cards */}



              {activeProgram &&
                (() => {
                  const program = activeProgram;
                  const index = activeProgramIndex;

                  return (
                    <div className="ml-program-editor">

                      {/* Start of Program Basic Info */}
                      <div className="ml-program-card">
                        <div className="ml-field-group">
                          <label className="ml-field-label">Program Title</label>
                          <input
                            className="ml-input"
                            type="text"
                            placeholder="Ex: Adult Basketball League"
                            value={program.title || ""}
                            onChange={(e) => updateProgram(index, "title", e.target.value)}
                          />
                        </div>

                        <div className="ml-field-group">
                          <label className="ml-field-label">Sport</label>
                          <select
                            className="ml-input"
                            value={program.sport}
                            onChange={(e) => updateProgram(index, "sport", e.target.value)}
                          >
                            <option value="">Select Sport</option>
                            {SPORTS.map((s) => (
                              <option key={s.key} value={s.key}>
                                {s.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="ml-field-group">
                          <label className="ml-field-label">Program Type</label>
                          <select
                            className="ml-input"
                            value={program.program_type}
                            onChange={(e) => updateProgram(index, "program_type", e.target.value)}
                          >
                            <option value="">Select Program Type</option>
                            {PROGRAM_TYPES.map((p) => (
                              <option key={p.key} value={p.key}>
                                {p.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="ml-field-group">
                          <label className="ml-field-label">Competition Level</label>
                          <select
                            className="ml-input"
                            value={program.competition_level || ""}
                            onChange={(e) => updateProgram(index, "competition_level", e.target.value)}
                          >
                            <option value="">Select Competition Level</option>
                            {COMPETITION_LEVELS.map((c) => (
                              <option key={c.key} value={c.key}>
                                {c.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="ml-program-audience-block">
                          <div className="ml-program-inline-label">Audience</div>

                          <div className="ml-program-audience-options">
                            {AUDIENCES.map((a) => {
                              const checked = (program.gender || []).includes(a.key);

                              return (
                                <label
                                  key={a.key}
                                  className={`ml-audience-pill ${checked ? "active" : ""}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => {
                                      const current = Array.isArray(program.gender)
                                        ? program.gender
                                        : [];

                                      const nextGender = e.target.checked
                                        ? [...current, a.key]
                                        : current.filter((g) => g !== a.key);

                                      updateProgram(index, "gender", nextGender);
                                    }}
                                  />
                                  <span>{a.label}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>

                        <div className="ml-field-group">
                          <label className="ml-field-label">Minimum Age</label>
                          <input
                            className="ml-input"
                            placeholder="Ex: 18"
                            value={program.min_age || ""}
                            onChange={(e) => updateProgram(index, "min_age", e.target.value)}
                          />
                        </div>

                        <div className="ml-field-group">
                          <label className="ml-field-label">Maximum Age</label>
                          <input
                            className="ml-input"
                            placeholder="Leave blank for 18+"
                            value={program.max_age || ""}
                            onChange={(e) => updateProgram(index, "max_age", e.target.value)}
                          />
                        </div>

                        <div className="ml-field-group ml-full-width">
                          <label className="ml-field-label">Program Description</label>
                          <textarea
                            className="ml-textarea"
                            placeholder="Briefly describe this program"
                            value={program.description || ""}
                            onChange={(e) => updateProgram(index, "description", e.target.value)}
                          />
                        </div>
                        {/* End of Program Basic Info */}




                        {/* Start of Program Contacts */}
                        <div className="ml-program-panel">
                          <button
                            type="button"
                            className="ml-program-panel-header"
                            onClick={() => toggleProgramPanel("contact")}
                          >
                            <div>
                              <strong>Program Contact</strong>
                              <p>Who users should contact about this program</p>
                            </div>

                            <span>{openProgramPanel === "contact" ? "▲" : "▼"}</span>
                          </button>

                          {openProgramPanel === "contact" && (
                            <div className="ml-program-panel-body">
                              <label className="ml-field-label">Contact Name</label>
                              <input
                                className="ml-input"
                                placeholder="Ex: Coach Mike, Program Director"
                                value={program.contact_name || ""}
                                onChange={(e) => updateProgram(index, "contact_name", e.target.value)}
                              />

                              <label className="ml-field-label">Contact Email</label>
                              <input
                                className="ml-input"
                                placeholder="program@example.com"
                                value={program.email || ""}
                                onChange={(e) => updateProgram(index, "email", e.target.value)}
                              />

                              <label className="ml-field-label">Contact Phone</label>
                              <input
                                className="ml-input"
                                placeholder="(555) 555-5555"
                                value={program.phone_number || ""}
                                onChange={(e) => updateProgram(index, "phone_number", e.target.value)}
                              />
                              <label className="ml-field-label">Contact Note</label>
                              <textarea
                                className="ml-textarea"
                                placeholder="Ex: Text preferred, ask for Coach Mike, call ext. 204"
                                value={program.contact_note || ""}
                                onChange={(e) => updateProgram(index, "contact_note", e.target.value)}
                              />
                            </div>
                          )}
                        </div>
                        {/* End of Program Contacts */}



                        {/* Start of Schedule Panel */}
                        {/* <div className="ml-program-panel">
                          <button
                            type="button"
                            className="ml-program-panel-header"
                            onClick={() => toggleProgramPanel("schedule")}
                          >
                            <div>
                              <strong>Schedule</strong>
                              <p>Days, times, and labels for this program</p>
                            </div>

                            <span>{openProgramPanel === "schedule" ? "▲" : "▼"}</span>
                          </button>

                          {openProgramPanel === "schedule" && (
                            <div className="ml-program-panel-body">
                              {(program.schedule_details || []).map((schedule, sIndex) => {
                                const isEditingSchedule = editingScheduleIndex === sIndex;

                                return (
                                  <div
                                    key={sIndex}
                                    className={isEditingSchedule ? "ml-schedule-row" : "ml-mini-card"}
                                  >
                                    {!isEditingSchedule ? (
                                      <>
                                        <div>
                                          <strong>{formatScheduleSummary(schedule)}</strong>
                                          <p>{schedule.label || "No label added"}</p>
                                        </div>

                                        <div className="ml-mini-card-actions">
                                          <button
                                            type="button"
                                            className="ml-small-action"
                                            onClick={() => setEditingScheduleIndex(sIndex)}
                                          >
                                            Edit
                                          </button>

                                          <button
                                            type="button"
                                            className="ml-small-danger"
                                            onClick={() => {
                                              const confirmed = window.confirm("Remove this schedule?");
                                              if (!confirmed) return;

                                              const next = [...form.programs];

                                              next[index] = {
                                                ...next[index],
                                                schedule_details: (next[index].schedule_details || []).filter(
                                                  (_, i) => i !== sIndex
                                                ),
                                              };

                                              setForm({ ...form, programs: next });
                                              setEditingScheduleIndex(null);
                                              setIsDirty(true);
                                            }}
                                          >
                                            Remove
                                          </button>
                                        </div>
                                      </>
                                    ) : (
                                      <>
                                        <div className="ml-field-group">
                                          <label className="ml-field-label">Start Time</label>
                                          <input
                                            className="ml-input"
                                            type="time"
                                            value={schedule.start_time || ""}
                                            onChange={(e) => {
                                              const next = [...form.programs];
                                              const schedules = [...(next[index].schedule_details || [])];

                                              schedules[sIndex] = {
                                                ...schedules[sIndex],
                                                start_time: e.target.value,
                                              };

                                              next[index] = {
                                                ...next[index],
                                                schedule_details: schedules,
                                              };

                                              setForm({ ...form, programs: next });
                                              setIsDirty(true);
                                            }}
                                          />
                                        </div>

                                        <div className="ml-field-group">
                                          <label className="ml-field-label">End Time</label>
                                          <input
                                            className="ml-input"
                                            type="time"
                                            value={schedule.end_time || ""}
                                            onChange={(e) => {
                                              const next = [...form.programs];
                                              const schedules = [...(next[index].schedule_details || [])];

                                              schedules[sIndex] = {
                                                ...schedules[sIndex],
                                                end_time: e.target.value,
                                              };

                                              next[index] = {
                                                ...next[index],
                                                schedule_details: schedules,
                                              };

                                              setForm({ ...form, programs: next });
                                              setIsDirty(true);
                                            }}
                                          />
                                        </div>

                                        <div className="ml-field-group ml-schedule-days-field">
                                          <label className="ml-field-label">Days</label>

                                          <div className="ml-day-options">
                                            {[
                                              "Monday",
                                              "Tuesday",
                                              "Wednesday",
                                              "Thursday",
                                              "Friday",
                                              "Saturday",
                                              "Sunday",
                                            ].map((day) => {
                                              const selectedDays = Array.isArray(schedule.days)
                                                ? schedule.days
                                                : schedule.day
                                                  ? [schedule.day]
                                                  : [];

                                              const checked = selectedDays.includes(day);

                                              return (
                                                <label
                                                  key={day}
                                                  className={`ml-day-pill ${checked ? "active" : ""}`}
                                                >
                                                  <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={(e) => {
                                                      const next = [...form.programs];
                                                      const schedules = [
                                                        ...(next[index].schedule_details || []),
                                                      ];

                                                      const currentDays = Array.isArray(
                                                        schedules[sIndex].days
                                                      )
                                                        ? schedules[sIndex].days
                                                        : schedules[sIndex].day
                                                          ? [schedules[sIndex].day]
                                                          : [];

                                                      const nextDays = e.target.checked
                                                        ? [...currentDays, day]
                                                        : currentDays.filter((d) => d !== day);

                                                      schedules[sIndex] = {
                                                        ...schedules[sIndex],
                                                        day: undefined,
                                                        days: nextDays,
                                                      };

                                                      next[index] = {
                                                        ...next[index],
                                                        schedule_details: schedules,
                                                      };

                                                      setForm({ ...form, programs: next });
                                                      setIsDirty(true);
                                                    }}
                                                  />

                                                  <span>{day.slice(0, 3)}</span>
                                                </label>
                                              );
                                            })}
                                          </div>
                                        </div>

                                        <div className="ml-field-group ml-schedule-label-field">
                                          <label className="ml-field-label">Schedule Label</label>
                                          <input
                                            className="ml-input"
                                            placeholder="Ex: Private Lesson, Open Play, Beginner Class"
                                            value={schedule.label || ""}
                                            onChange={(e) => {
                                              const next = [...form.programs];
                                              const schedules = [...(next[index].schedule_details || [])];

                                              schedules[sIndex] = {
                                                ...schedules[sIndex],
                                                label: e.target.value,
                                              };

                                              next[index] = {
                                                ...next[index],
                                                schedule_details: schedules,
                                              };

                                              setForm({ ...form, programs: next });
                                              setIsDirty(true);
                                            }}
                                          />
                                        </div>

                                        <div className="ml-edit-actions">
                                          <button
                                            type="button"
                                            className="ml-small-action"
                                            onClick={() => setEditingScheduleIndex(null)}
                                          >
                                            Done
                                          </button>

                                          <button
                                            type="button"
                                            className="ml-small-danger"
                                            onClick={() => {
                                              const confirmed = window.confirm("Remove this schedule?");
                                              if (!confirmed) return;

                                              const next = [...form.programs];

                                              next[index] = {
                                                ...next[index],
                                                schedule_details: (next[index].schedule_details || []).filter(
                                                  (_, i) => i !== sIndex
                                                ),
                                              };

                                              setForm({ ...form, programs: next });
                                              setEditingScheduleIndex(null);
                                              setIsDirty(true);
                                            }}
                                          >
                                            Remove
                                          </button>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                );
                              })}

                              <button
                                type="button"
                                className="ml-btn-secondary"
                                onClick={() => {
                                  const next = [...form.programs];

                                  const nextSchedules = [
                                    ...(next[index].schedule_details || []),
                                    {
                                      days: [],
                                      start_time: "",
                                      end_time: "",
                                      label: "",
                                    },
                                  ];

                                  next[index] = {
                                    ...next[index],
                                    schedule_details: nextSchedules,
                                  };

                                  setForm({ ...form, programs: next });
                                  setEditingScheduleIndex(nextSchedules.length - 1);
                                  setIsDirty(true);
                                }}
                              >
                                + Add Schedule
                              </button>
                            </div>
                          )}
                        </div> */}
                        {/* End of Schedule Panel */}

                        {/* Start of Venues Panel */}
                        <div className="ml-program-panel">
                          <button
                            type="button"
                            className="ml-program-panel-header"
                            onClick={() => toggleProgramPanel("venues")}
                          >
                            <div>
                              <strong>Locations / Venues</strong>
                              <p>Add where this program takes place</p>
                            </div>

                            <span>{openProgramPanel === "venues" ? "▲" : "▼"}</span>
                          </button>

                          {openProgramPanel === "venues" && (
                            <div className="ml-program-panel-body">
                              <div className="ml-venues">
                                {(program.venues || []).map((venue, vIndex) => {
                                  const isEditingVenue = editingVenueIndex === vIndex;

                                  return (
                                    <div
                                      key={vIndex}
                                      className={isEditingVenue ? "ml-venue-row" : "ml-mini-card"}
                                    >
                                      {!isEditingVenue ? (
                                        <>
                                          <div>
                                            <strong>{venue.name || "Untitled venue"}</strong>
                                            {venue.map_url && <p>Map link added</p>}
                                          </div>

                                          <div className="ml-mini-card-actions">
                                            <button
                                              type="button"
                                              className="ml-small-action"
                                              onClick={() => setEditingVenueIndex(vIndex)}
                                            >
                                              Edit
                                            </button>

                                            <button
                                              type="button"
                                              className="ml-small-danger"
                                              onClick={() => {
                                                const confirmed = window.confirm("Remove this venue?");
                                                if (!confirmed) return;

                                                const next = [...form.programs];

                                                next[index] = {
                                                  ...next[index],
                                                  venues: (next[index].venues || []).filter(
                                                    (_, i) => i !== vIndex
                                                  ),
                                                };

                                                setForm({ ...form, programs: next });
                                                setEditingVenueIndex(null);
                                                setIsDirty(true);
                                              }}
                                            >
                                              Remove
                                            </button>
                                          </div>
                                        </>
                                      ) : (
                                        <>
                                          <div className="ml-field-group">
                                            <label className="ml-field-label">Venue Name</label>
                                            <input
                                              className="ml-input"
                                              placeholder="Ex: Tustin Rec Center"
                                              value={venue.name || ""}
                                              onChange={(e) => {
                                                const next = [...form.programs];
                                                const venues = [...(next[index].venues || [])];

                                                venues[vIndex] = {
                                                  ...venues[vIndex],
                                                  name: e.target.value,
                                                };

                                                next[index] = {
                                                  ...next[index],
                                                  venues,
                                                };

                                                setForm({ ...form, programs: next });
                                                setIsDirty(true);
                                              }}
                                            />
                                          </div>

                                          <div className="ml-field-group">
                                            <label className="ml-field-label">Google Map URL</label>
                                            <input
                                              className="ml-input"
                                              placeholder="Optional Google Map link"
                                              value={venue.map_url || ""}
                                              onChange={(e) => {
                                                const next = [...form.programs];
                                                const venues = [...(next[index].venues || [])];

                                                venues[vIndex] = {
                                                  ...venues[vIndex],
                                                  map_url: e.target.value,
                                                };

                                                next[index] = {
                                                  ...next[index],
                                                  venues,
                                                };

                                                setForm({ ...form, programs: next });
                                                setIsDirty(true);
                                              }}
                                            />
                                          </div>

                                          <div className="ml-edit-actions">
                                            <button
                                              type="button"
                                              className="ml-small-action"
                                              onClick={() => setEditingVenueIndex(null)}
                                            >
                                              Done
                                            </button>

                                            <button
                                              type="button"
                                              className="ml-small-danger"
                                              onClick={() => {
                                                const confirmed = window.confirm("Remove this venue?");
                                                if (!confirmed) return;

                                                const next = [...form.programs];

                                                next[index] = {
                                                  ...next[index],
                                                  venues: (next[index].venues || []).filter(
                                                    (_, i) => i !== vIndex
                                                  ),
                                                };

                                                setForm({ ...form, programs: next });
                                                setEditingVenueIndex(null);
                                                setIsDirty(true);
                                              }}
                                            >
                                              Remove
                                            </button>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  );
                                })}

                                <button
                                  className="ml-btn-secondary"
                                  onClick={() => {
                                    const next = [...form.programs];
                                    const nextVenues = [
                                      ...(next[index].venues || []),
                                      { name: "", map_url: "" },
                                    ];

                                    next[index] = {
                                      ...next[index],
                                      venues: nextVenues,
                                    };

                                    setForm({ ...form, programs: next });
                                    setEditingVenueIndex(nextVenues.length - 1);
                                    setIsDirty(true);
                                  }}
                                >
                                  + Add Venue
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        {/* End of Venue Panel */}

                        {/* Start of Registration Panel */}
                        {/* <div className="ml-program-panel">
                          <button
                            type="button"
                            className="ml-program-panel-header"
                            onClick={() => toggleProgramPanel("registration")}
                          >
                            <div>
                              <strong>Registration / Booking</strong>
                              <p>Price, booking link, status, and dates</p>
                            </div>

                            <span>{openProgramPanel === "registration" ? "▲" : "▼"}</span>
                          </button>

                          {openProgramPanel === "registration" && (
                            <div className="ml-program-panel-body">
                              <label className="ml-field-label">Price / Fee</label>
                              <input
                                className="ml-input"
                                placeholder="Ex: $40 per lesson, Free, Contact for pricing"
                                value={program.price || ""}
                                onChange={(e) => updateProgram(index, "price", e.target.value)}
                              />

                              <label className="ml-field-label">Registration / Booking URL</label>
                              <input
                                className="ml-input"
                                placeholder="https://..."
                                value={program.registration_url || ""}
                                onChange={(e) =>
                                  updateProgram(index, "registration_url", e.target.value)
                                }
                              />

                              <label className="ml-field-label">Registration Status</label>
                              <input
                                className="ml-input"
                                placeholder="Ex: Open, Closed, Waitlist"
                                value={program.registration_status || ""}
                                onChange={(e) =>
                                  updateProgram(index, "registration_status", e.target.value)
                                }
                              />

                              <label className="ml-field-label">Registration Deadline</label>
                              <input
                                className="ml-input"
                                type="date"
                                value={program.registration_deadline || ""}
                                onChange={(e) =>
                                  updateProgram(index, "registration_deadline", e.target.value)
                                }
                              />

                              <label className="ml-field-label">Program Start Date</label>
                              <input
                                className="ml-input"
                                type="date"
                                value={program.start_date || ""}
                                onChange={(e) => updateProgram(index, "start_date", e.target.value)}
                              />

                              <label className="ml-field-label">Program End Date</label>
                              <input
                                className="ml-input"
                                type="date"
                                value={program.end_date || ""}
                                onChange={(e) => updateProgram(index, "end_date", e.target.value)}
                              />

                              <label className="ml-field-label">Season</label>
                              <input
                                className="ml-input"
                                placeholder="Ex: Spring 2026, Summer Camp"
                                value={program.season || ""}
                                onChange={(e) => updateProgram(index, "season", e.target.value)}
                              />

                              <label className="ml-field-label">Program Notes</label>
                              <textarea
                                className="ml-textarea"
                                placeholder="Anything users should know before registering"
                                value={program.notes || ""}
                                onChange={(e) => updateProgram(index, "notes", e.target.value)}
                              />
                            </div>
                          )}
                        </div> */}
                        {/* End of Registration Panel */}


                      </div>
                    </div>
                  );
                })()}
            </div>
          </div>

        )}

        
      </div>

      {/* {canShowLeagueTrial && (
        <div className="ml-card">
          <div className="ml-card-header">
            <div>
              <h3>
                {isTrialPlan ? "League Management Trial Active" : "Try League Management"}
              </h3>
              <p>
                Create seasons, leagues, teams, players, schedules, standings, games,
                results, and stats.
              </p>
            </div>

            <button
              className="ml-btn-primary"
              onClick={isTrialPlan ? () => navigate("/dashboard/seasons") : handleStartTrial}
            >
              {isTrialPlan ? "Go to League Dashboard" : "Start Free Trial"}
            </button>
          </div>
        </div>
      )} */}
    </div>
  );
}

export default ManageListing;